import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
          }) => void;
          renderButton: (
            container: HTMLElement,
            options: Record<string, unknown>
          ) => void;
        };
      };
    };
  }
}

export default function LoginPage() {
  const { user, loading, googleClientId, login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!googleClientId || !buttonRef.current) return;
    let cancelled = false;

    const waitForGoogle = () =>
      new Promise<void>((resolve) => {
        const tick = () => {
          if (window.google?.accounts?.id) return resolve();
          setTimeout(tick, 50);
        };
        tick();
      });

    (async () => {
      await waitForGoogle();
      if (cancelled || !buttonRef.current) return;
      window.google!.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          setError(null);
          try {
            await login(response.credential);
          } catch (e) {
            const msg = e instanceof Error ? e.message : "Sign-in failed";
            setError(msg);
          }
        },
        auto_select: false,
      });
      window.google!.accounts.id.renderButton(buttonRef.current, {
        type: "standard",
        theme: "filled_blue",
        size: "large",
        shape: "pill",
        text: "signin_with",
        logo_alignment: "left",
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [googleClientId, login]);

  if (loading)
    return (
      <div style={{ padding: "var(--space-7)", textAlign: "center" }}>Loading…</div>
    );
  if (user) return <Navigate to="/landing" replace />;

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        padding: "var(--space-5)",
      }}
    >
      <div
        className="card"
        style={{
          padding: "var(--space-7) var(--space-6)",
          maxWidth: 460,
          width: "100%",
          textAlign: "center",
        }}
      >
        <div
          className="brand-mark"
          style={{ justifyContent: "center", fontSize: "1.4rem", marginBottom: "var(--space-3)" }}
        >
          TapVocab
        </div>
        <h1 style={{ fontSize: "1.75rem", marginBottom: "var(--space-2)" }}>
          Tap, hear, learn.
        </h1>
        <p
          style={{
            color: "var(--text-muted)",
            marginBottom: "var(--space-6)",
            lineHeight: 1.5,
          }}
        >
          Sign in with Google to start exploring vocabulary in context.
        </p>
        <div style={{ display: "grid", placeItems: "center" }}>
          <div ref={buttonRef} />
        </div>
        {error && (
          <div
            style={{
              marginTop: "var(--space-4)",
              padding: "var(--space-3) var(--space-4)",
              borderRadius: "var(--radius-md)",
              background: "rgba(239, 68, 68, 0.1)",
              color: "var(--accent-red)",
              fontSize: "0.875rem",
            }}
          >
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
